import { type RefObject, useCallback, useState } from 'react';

export type ResizePosition =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right';

const MIN_IMAGE_SIZE = 150;

interface UseImageResizeOptions {
  imageRef: RefObject<HTMLImageElement>;
  onResizeComplete: (dimensions: { width: number }) => void;
}

export const useImageResize = ({
  imageRef,
  onResizeComplete,
}: UseImageResizeOptions) => {
  const [isResizing, setIsResizing] = useState(false);

  const handleResizeStart = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, position: ResizePosition) => {
      event.preventDefault();
      event.stopPropagation();

      const image = imageRef.current;
      if (!image) return;

      const startX = event.clientX;
      const startWidth = image.offsetWidth;

      setIsResizing(true);

      const handleMouseMove = (e: MouseEvent) => {
        let deltaX = e.clientX - startX;

        // Invert delta for left-side handles
        if (position.includes('left')) {
          deltaX = -deltaX;
        }

        const newWidth = Math.max(MIN_IMAGE_SIZE, startWidth + deltaX);

        image.style.width = `${newWidth}px`;
      };

      const handleMouseUp = () => {
        setIsResizing(false);

        if (image) {
          onResizeComplete({
            width: image.offsetWidth,
          });
        }

        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [imageRef, onResizeComplete],
  );

  return {
    isResizing,
    handleResizeStart,
  };
};
